/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import * as React from 'react';
import { useLocation } from '@reach/router';
import moment from 'moment';
import styled from 'styled-components';

import { INTERNAL_LINKS, navigate } from '../../utils';
import { Text } from '../design-system';

import {
  type RecommendedBlogPost,
  type RecommendedUseCase,
  type RecommendedUseCaseCategory
} from './Recommendations';
import {
  GridItemWrapper,
  StyledInfo,
  StyledPreviewImage,
  Title
} from './sharedStyles';

const Wrapper = styled.div`
  width: calc(100% / 3);
  flex: 0 1 calc(100% / 3);
  padding: 10px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex: 0 1 calc(100% / 2);
    width: calc(100% / 2);
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    flex: 0 1 100%;
    width: 100%;
`;

const StyledGridWrapper = styled(GridItemWrapper)`
  height: 100%;
`;

const CategoryTag = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 6px 10px;
  background: ${({ theme }) => theme.backgroundColor};
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  letter-spacing: 1px;
`;

const Author = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  margin-bottom: 10px;
`;

interface UseCaseCategoryCardProps {
  item: RecommendedUseCaseCategory;
}

interface BlogPostCardProps {
  item: RecommendedBlogPost;
}

interface UseCaseCardProps {
  item: RecommendedUseCase;
}

export const BlogPostCard: React.ComponentType<BlogPostCardProps> = React.memo(
  ({ item }) => {
    const { search } = useLocation();

    const url = useMemo(() => {
      return `${INTERNAL_LINKS.Blog}${item.slug}`;
    }, [item.slug]);

    return (
      <Wrapper
        onClick={() => {
          navigate(url, search);
        }}>
        <StyledGridWrapper>
          {item.heroImage && (
            <StyledPreviewImage>
              <CategoryTag>BLOG</CategoryTag>
              <img
                alt={item.heroImage.title}
                loading="lazy"
                src={item.heroImage.url}
              />
            </StyledPreviewImage>
          )}
          <StyledInfo>
            <Title renderAs="h4">{item.title}</Title>
            <Author>
              {item.author} · {moment(item.date).format('MMMM D, YYYY')}
            </Author>
            <Text size="medium">{item.seoMetaDescription}</Text>
          </StyledInfo>
        </StyledGridWrapper>
      </Wrapper>
    );
  }
);

export const UseCaseCategoryCard: React.ComponentType<UseCaseCategoryCardProps> =
  React.memo(({ item }) => {
    const { search } = useLocation();

    const url = useMemo(() => {
      return `${INTERNAL_LINKS.Templates}${item.slug}`;
    }, [item.slug]);

    return (
      <Wrapper
        onClick={() => {
          navigate(url, search);
        }}>
        <StyledGridWrapper>
          <StyledPreviewImage>
            <CategoryTag>USE CASES</CategoryTag>
            <img
              alt={
                item.gridImage
                  ? item.gridImage.title
                  : item.socialSharePreviewImage?.title
              }
              loading="lazy"
              src={
                item.gridImage
                  ? item.gridImage.url
                  : item.socialSharePreviewImage?.url
              }
            />
          </StyledPreviewImage>
          <StyledInfo>
            <Title renderAs="h4">{item.title}</Title>
            <Author>Multiple contributors</Author>
            <Text size="medium">{item.seoMetaDescription}</Text>
          </StyledInfo>
        </StyledGridWrapper>
      </Wrapper>
    );
  });

export const UseCaseCard: React.ComponentType<UseCaseCardProps> = React.memo(
  ({ item }) => {
    const { search } = useLocation();

    const url = useMemo(() => {
      return `${INTERNAL_LINKS.Templates}${item.category.slug}/${item.slug}`;
    }, [item.category, item.slug]);

    return (
      <Wrapper
        onClick={() => {
          navigate(url, search);
        }}>
        <StyledGridWrapper>
          {item.gridImage && (
            <StyledPreviewImage>
              <CategoryTag>TEMPLATE</CategoryTag>
              <img
                alt={item.gridImage.title}
                loading="lazy"
                src={item.gridImage.url}
              />
            </StyledPreviewImage>
          )}
          <StyledInfo>
            <Title renderAs="h4">{item.name}</Title>
            {item.author && <Author>{item.author}</Author>}
            <Text size="medium">{item.seoMetaDescription}</Text>
          </StyledInfo>
        </StyledGridWrapper>
      </Wrapper>
    );
  }
);
